<script setup lang="ts">
  import {
    computed, onMounted, ref 
  } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { useContentApi } from '@/composables/useCmsContentApi'
  import { useAppRouting } from '@/composables/useAppRouting'
  import useRelation from '@/composables/useRelation'
  import RouteParam from '@/enums/RouteParam'
  import type { Author } from '@/types/Author'
  import type { TopSection } from '@/types/TopSection'

  import Byline from '@/components/common/Byline.vue'
  import ImageWithCaption from '@/components/common/ImageWithCaption.vue'
  import Typography from '@/components/common/Typography.vue'
  import { useImage } from '@/composables/useImage'
  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'
  import VgsSpinner from '@/components/common/VgsSpinner.vue'

  const props = defineProps<{
    element: TopSection
  }>()

  const { t } = useI18n()
  const { getRelatedContentFields } = useRelation()
  const { getContentIdFromRouteParam } = useAppRouting()
  const { getCurrentTopic } = useContentApi()
  const { src, altText, copyright, caption, isLoading } = useImage({ imageId: props.element.image?.destinationContentId ?? '' })

  const authors = ref<Author[]>([])
  const label = ref<string>('')

  const getTopicTitle = async () => {
    const topic = await getCurrentTopic(getContentIdFromRouteParam(RouteParam.Topic) as number)
    return topic?.title || ''
  }

  const getAuthors = async () => await getRelatedContentFields<Author>(props.element.authorIds)

  onMounted(async () => {
    label.value = await getTopicTitle()
    if (!props?.element.authorIds?.length) return
    authors.value = await getAuthors()
  })

  const introductionIncludesParagraph = props.element.introduction?.includes('</p>')
  const noWrapperClass = computed(() => !src.value && 'no-image')
</script>

<template>
  <section class="wrapper">
    <VgsSpinner
      v-if="isLoading" 
      class="vgs-spinner"
    />
    <div
      v-else
      :class="['top-section-element', element.colorPair, noWrapperClass]"
    >
      <div class="text-container">
        <div class="content">
          <Typography
            variant="label"
            class="label"
          >
            {{ label }}
          </Typography>
          <Typography
            variant="h1"
            class="title"
          >
            {{ element.title }}
          </Typography>
          <RichTextRenderer
            v-if="introductionIncludesParagraph"
            class="rich-text-introduction"
            :rich-text="element.introduction"
          />
          <Typography v-else variant="preamble">
            {{ element.introduction }}
          </Typography>
        </div>
        <Byline
          v-if="authors.length"
          class="byline"
          :authors="authors"
          :prefix="t('authors')"
          variant="text-only"
          no-lines
        />
      </div>

      <div
        v-if="src" 
        class="image-container"
      >
        <ImageWithCaption
          :src="src"
          :caption="caption"
          :alt-text="altText || ''"
          :copyright="copyright"
          object-fit="cover"
          image-variation="banner_large"
          class="image"
        />
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
  @include color-pairs;

  $padding-mobile: 24px;
  $padding-desktop: 40px;

  .wrapper {
    max-height: 914px;

    &:not(:has(.no-image)) {
      height: 100vh;
    }
  }

  .top-section-element {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
    background-color: var(--color-pair-background-color);
    color: var(--color-pair-color);
    height: 100%;

    @include vgs-media-mobile-and-tablet {
      grid-auto-flow: row;
      grid-auto-rows: 1fr;
      max-height: unset;
      height: fit-content;
    }

    .text-container,
    .image-container {
      max-height: inherit;
    }

    .text-container {
      @include vgs-padding-text-element;

      display: flex;
      flex-direction: column;
      justify-content: center;

      @include vgs-media-mobile {
        padding-top: 80px;
      }
    }

    .image-container {
      overflow: hidden;

      @include vgs-media-mobile-and-tablet {
        aspect-ratio: 1;
      }
    }

    &.no-image {
      height: unset;

      .title {
        margin-bottom: 0;
      }

      .text-container {
        margin: 0 auto;
        padding: 80px 0;
        width: 100%;

        @include vgs-media-tablet-and-desktop {
          width: $vgs-max-width-content;
        }

        @include vgs-media-tablet {
          padding-bottom: 40px;
        }

        @include horizontal-padding-mobile;
      }
    }

    .content {
      display: flex;
      flex-direction: column;
      justify-content: center;
      height: 100%;

      .label {
        margin-bottom: 4px;
        min-height: 26px;
      }

      .rich-text-introduction {
        @include preamble;
      }

      .byline {
        margin-top: 64px;
      }
    }

    p {
      margin: 0;
    }
  }

  .image {
    height: 100%;
  }
</style>
